import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  CreditCard,
  Shield,
  Users,
  UserCheck,
  UserX,
  AlertTriangle,
  CheckCircle,
  DollarSign,
  Clock,
  Search,
  Loader2,
  Info,
  X,
  Plus,
  Settings2
} from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";
import { toast } from "sonner";

const PrepaymentSettings = () => {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [settings, setSettings] = useState({
    require_prepay_all: false,
    allow_pay_at_appointment: true,
    prepay_clients: []
  });
  const [clients, setClients] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [showAddClientDialog, setShowAddClientDialog] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);

  useEffect(() => {
    fetchSettings();
    fetchClients();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await axios.get(`${API}/payments/prepay/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
    } catch (error) {
      console.error("Failed to fetch prepay settings:", error);
      toast.error("Failed to load prepayment settings");
    } finally {
      setLoading(false);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await axios.get(`${API}/clients/my-clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClients(response.data.clients || response.data || []);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/payments/prepay/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Prepayment settings saved!");
    } catch (error) {
      console.error("Failed to save settings:", error);
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const addClientToPrepay = async (clientId) => {
    try {
      await axios.put(`${API}/payments/prepay/client`, {
        client_id: clientId,
        require_prepay: true
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({
        ...prev,
        prepay_clients: [...prev.prepay_clients, clientId]
      }));
      toast.success("Client added to prepay list");
      setShowAddClientDialog(false);
    } catch (error) {
      toast.error("Failed to add client");
    }
  };

  const removeClientFromPrepay = async (clientId) => {
    try {
      await axios.put(`${API}/payments/prepay/client`, {
        client_id: clientId,
        require_prepay: false
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(prev => ({
        ...prev,
        prepay_clients: prev.prepay_clients.filter(id => id !== clientId)
      }));
      toast.success("Client removed from prepay list");
    } catch (error) {
      toast.error("Failed to remove client");
    }
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.client_id === clientId || c.user_id === clientId);
    return client?.name || client?.client_name || "Unknown Client";
  };

  const filteredClients = clients.filter(c => {
    const name = c.name || c.client_name || "";
    const email = c.email || "";
    const query = searchQuery.toLowerCase();
    return name.toLowerCase().includes(query) || email.toLowerCase().includes(query);
  });

  const availableClients = filteredClients.filter(c => {
    const clientId = c.client_id || c.user_id;
    return !settings.prepay_clients.includes(clientId);
  });

  if (loading) {
    return (
      <Card className="bg-stone-800/50 border-stone-700">
        <CardContent className="py-12 text-center">
          <Loader2 className="w-8 h-8 animate-spin mx-auto text-amber-500" />
          <p className="text-stone-400 mt-2">Loading prepayment settings...</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6" data-testid="prepayment-settings">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center">
            <CreditCard className="w-6 h-6 text-white" />
          </div>
          <div>
            <h2 className="text-xl font-bold text-white">Prepayment Settings</h2>
            <p className="text-stone-400 text-sm">Control when clients pay for appointments</p>
          </div>
        </div>
        <Button
          onClick={saveSettings}
          disabled={saving}
          className="bg-green-600 hover:bg-green-700 text-white"
          data-testid="save-prepay-settings-btn"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <CheckCircle className="w-4 h-4 mr-2" />
              Save Settings
            </>
          )}
        </Button>
      </div>

      {/* Info Banner */}
      <Card className="bg-blue-500/10 border-blue-500/30">
        <CardContent className="p-4 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
          <div className="text-sm">
            <p className="text-blue-300 font-medium mb-1">How Prepayment Works</p>
            <p className="text-blue-200/80">
              When prepayment is required, clients must pay before their appointment is confirmed. 
              This reduces no-shows and ensures you&apos;re compensated for your time. You can require 
              prepayment for all clients or just specific ones.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Main Settings */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Global Settings */}
        <Card className="bg-stone-800/50 border-stone-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Settings2 className="w-5 h-5 text-amber-500" />
              Global Settings
            </CardTitle>
            <CardDescription className="text-stone-400">
              Apply prepayment rules to all bookings
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Require Prepay All */}
            <div className="flex items-center justify-between p-4 bg-stone-700/30 rounded-lg">
              <div className="flex items-start gap-3">
                <Shield className="w-5 h-5 text-amber-500 mt-0.5" />
                <div>
                  <Label className="text-white font-medium">Require Prepayment for All Clients</Label>
                  <p className="text-stone-400 text-sm mt-1">
                    Every client must pay upfront when booking
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.require_prepay_all}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, require_prepay_all: checked }))}
                className="data-[state=checked]:bg-amber-500"
                data-testid="require-prepay-all-switch"
              />
            </div>

            {/* Allow Pay at Appointment */}
            <div className="flex items-center justify-between p-4 bg-stone-700/30 rounded-lg">
              <div className="flex items-start gap-3">
                <Clock className="w-5 h-5 text-blue-500 mt-0.5" />
                <div>
                  <Label className="text-white font-medium">Allow Pay at Appointment</Label>
                  <p className="text-stone-400 text-sm mt-1">
                    Let non-prepay clients pay when they arrive
                  </p>
                </div>
              </div>
              <Switch
                checked={settings.allow_pay_at_appointment}
                onCheckedChange={(checked) => setSettings(prev => ({ ...prev, allow_pay_at_appointment: checked }))}
                className="data-[state=checked]:bg-blue-500"
                disabled={settings.require_prepay_all}
                data-testid="allow-pay-at-appointment-switch"
              />
            </div>

            {settings.require_prepay_all && (
              <div className="p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg">
                <p className="text-amber-400 text-sm flex items-center gap-2">
                  <AlertTriangle className="w-4 h-4" />
                  All clients must prepay. Individual client settings are ignored.
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Stats Card */}
        <Card className="bg-stone-800/50 border-stone-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <DollarSign className="w-5 h-5 text-green-500" />
              Prepayment Stats
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="p-4 bg-stone-700/30 rounded-lg text-center">
                <div className="text-3xl font-bold text-white">
                  {settings.prepay_clients.length}
                </div>
                <div className="text-stone-400 text-sm">Clients on Prepay</div>
              </div>
              <div className="p-4 bg-stone-700/30 rounded-lg text-center">
                <div className="text-3xl font-bold text-white">
                  {clients.length}
                </div>
                <div className="text-stone-400 text-sm">Total Clients</div>
              </div>
            </div>

            <div className="p-4 bg-gradient-to-r from-green-500/10 to-emerald-500/5 rounded-lg border border-green-500/20">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-5 h-5 text-green-400" />
                <span className="text-green-400 font-medium">Benefits of Prepayment</span>
              </div>
              <ul className="text-stone-300 text-sm space-y-1">
                <li>• Reduces no-shows by up to 80%</li>
                <li>• Guarantees payment for your time</li>
                <li>• Filters out uncommitted clients</li>
                <li>• Improves cash flow predictability</li>
              </ul>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Client-Specific Prepay */}
      <Card className="bg-stone-800/50 border-stone-700">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-white flex items-center gap-2">
                <Users className="w-5 h-5 text-purple-500" />
                Client-Specific Prepayment
              </CardTitle>
              <CardDescription className="text-stone-400">
                Require specific clients to prepay while others can pay at appointment
              </CardDescription>
            </div>
            <Button
              onClick={() => setShowAddClientDialog(true)}
              disabled={settings.require_prepay_all}
              className="bg-purple-600 hover:bg-purple-700"
              data-testid="add-prepay-client-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Add Client
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {settings.require_prepay_all ? (
            <div className="text-center py-8">
              <AlertTriangle className="w-12 h-12 mx-auto text-amber-500 mb-3" />
              <p className="text-stone-300">Global prepayment is enabled</p>
              <p className="text-stone-500 text-sm">All clients already require prepayment</p>
            </div>
          ) : settings.prepay_clients.length === 0 ? (
            <div className="text-center py-8">
              <UserCheck className="w-12 h-12 mx-auto text-stone-600 mb-3" />
              <p className="text-stone-400">No clients on prepay list</p>
              <p className="text-stone-500 text-sm">Click &quot;Add Client&quot; to require specific clients to prepay</p>
            </div>
          ) : (
            <div className="space-y-2">
              {settings.prepay_clients.map((clientId) => (
                <div
                  key={clientId}
                  className="flex items-center justify-between p-3 bg-stone-700/30 rounded-lg"
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-purple-500/20 flex items-center justify-center">
                      <UserCheck className="w-5 h-5 text-purple-400" />
                    </div>
                    <div>
                      <p className="text-white font-medium">{getClientName(clientId)}</p>
                      <Badge className="bg-amber-500/20 text-amber-400 text-xs">
                        Prepay Required
                      </Badge>
                    </div>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeClientFromPrepay(clientId)}
                    className="text-red-400 hover:text-red-300 hover:bg-red-500/10"
                    data-testid={`remove-prepay-client-${clientId}`}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Add Client Dialog */}
      <Dialog open={showAddClientDialog} onOpenChange={setShowAddClientDialog}>
        <DialogContent className="bg-stone-800 border-stone-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">Add Client to Prepay List</DialogTitle>
            <DialogDescription className="text-stone-400">
              Select a client to require prepayment for their bookings
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-500" />
              <Input
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search clients..."
                className="pl-9 bg-stone-700 border-stone-600 text-white"
              />
            </div>

            {/* Client List */}
            <div className="max-h-64 overflow-y-auto space-y-2">
              {availableClients.length === 0 ? (
                <p className="text-stone-500 text-center py-4">
                  {searchQuery ? "No clients found" : "All clients are already on prepay list"}
                </p>
              ) : (
                availableClients.map((client) => {
                  const clientId = client.client_id || client.user_id;
                  const clientName = client.name || client.client_name || "Unknown";
                  return (
                    <div
                      key={clientId}
                      className="flex items-center justify-between p-3 bg-stone-700/30 rounded-lg hover:bg-stone-700/50 cursor-pointer transition-colors"
                      onClick={() => addClientToPrepay(clientId)}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-stone-600 flex items-center justify-center">
                          <span className="text-white font-medium">
                            {clientName.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div>
                          <p className="text-white font-medium">{clientName}</p>
                          {client.email && (
                            <p className="text-stone-400 text-sm">{client.email}</p>
                          )}
                        </div>
                      </div>
                      <Plus className="w-5 h-5 text-stone-400" />
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PrepaymentSettings;
